import { ApolloError } from "@apollo/client";
import {
  MagicRulesFileId,
  OrgGuideFileId,
  OrgGuideFilePath,
  OrgGuideFileVersionId,
  OrgId,
  UserId,
} from "@hex/common";

import { LoadableState } from "../../../util/loadableState";

// --- Entity ---

export type ContextWorkbenchEntity =
  | ContextWorkbenchEntity.MagicRulesFile
  | ContextWorkbenchEntity.OrgGuideFile;

/**
 * A entity in the context workbench represents some piece of data that can be
 * uniquely identified by the client and server. This can be either single-player
 * or multi-player entities.
 */
export namespace ContextWorkbenchEntity {
  export type Type = Type.MagicRulesFile | Type.OrgGuideFile;

  export namespace Type {
    export const MagicRulesFile = "MagicRulesFile" as const;
    export type MagicRulesFile = typeof Type.MagicRulesFile;

    export const OrgGuideFile = "OrgGuideFile" as const;
    export type OrgGuideFile = typeof Type.OrgGuideFile;
  }

  export type MagicRulesFile = {
    type: Type.MagicRulesFile;
    orgId: OrgId;
    userId: UserId | null;
    magicRulesFileId: MagicRulesFileId;
    filename: string;
    fileContent: string | null;
    sizeBytes: number | null;
  };

  /**
   * Entity shape for OrgGuideFile.
   */
  export type OrgGuideFile = {
    type: Type.OrgGuideFile;
    orgId: OrgId;
    orgGuideFileId: OrgGuideFileId;
    orgGuideFileVersionId: OrgGuideFileVersionId;
    filePath: OrgGuideFilePath;
    contents: string | null;
  };
}

// --- Entities aggregate ---

/**
 * An object that acts as the source of truth for single-player entities within
 * the context workbench. Note that only `magicRulesFile` are included here, since
 * `orgGuideFile`s are stored in a separate MP slice.
 */
export type ContextWorkbenchEntities = {
  magicRulesFile: LoadableState<
    ContextWorkbenchEntity.MagicRulesFile,
    ApolloError
  >;
};

export function getInitialContextWorkbenchEntities(): ContextWorkbenchEntities {
  return {
    magicRulesFile: {
      loading: false,
      error: undefined,
      value: undefined,
    },
  };
}

// --- EntityRef ---

export type ContextWorkbenchEntityRef =
  | ContextWorkbenchEntityRef.MagicRulesFile
  | ContextWorkbenchEntityRef.OrgGuideFile;

/**
 * A lightweight pointer to an entity that's often embedded in other data
 * structures. It contains exactly enough information to fetch the full entity
 * from its respective source of truth.
 */
export namespace ContextWorkbenchEntityRef {
  export type MagicRulesFile = {
    type: ContextWorkbenchEntity.Type.MagicRulesFile;
    orgId: OrgId;
    userId: UserId | null;
    magicRulesFileId: MagicRulesFileId;
  };

  export type OrgGuideFile = {
    type: ContextWorkbenchEntity.Type.OrgGuideFile;
    orgId: OrgId;
    orgGuideFileId: OrgGuideFileId;
    orgGuideFileVersionId: OrgGuideFileVersionId;
  };
}
